/**
 * TriggerableSynth-compatible wrapper for WASM Mono Synth
 * Replaces Tone.js MonoSynth with WASM-based implementation
 *
 * Does NOT depend on Tone.js - uses native AudioContext and durationUtils
 */

import { MonoSynthBridge, type MonoSynthParams } from './mono-synth-bridge';
import { noteNameToMidi, durationToSeconds } from '../audio/durationUtils';

// Re-export TriggerableSynth interface for convenience
export interface TriggerableSynth {
  triggerAttackRelease(
    note: string | number,
    duration: number,
    time?: number,
    velocity?: number
  ): void;
  triggerRelease(time?: number): void;
}

// Volume control interface
interface VolumeControl {
  value: number;
  rampTo: (value: number, rampTime: number, startTime?: number) => void;
}

// Convert decibels to linear gain
function dbToGain(db: number): number {
  return Math.pow(10, db / 20);
}

// Convert linear gain to decibels
function gainToDb(gain: number): number {
  return 20 * Math.log10(Math.max(gain, 0.0001));
}

export class WASMMonoSynth implements TriggerableSynth {
  private bridge: MonoSynthBridge;
  private context: AudioContext;
  private gainNode: GainNode;
  private currentNote: number | null = null;
  private _detuneValue: number = 0;
  private _bpm: number = 120;
  private initialized: boolean = false;

  // Tone.js-compatible properties
  public volume: VolumeControl;
  public detune: { value: number };
  public portamento: number = 0;

  constructor(context: AudioContext) {
    this.context = context;
    this.bridge = new MonoSynthBridge(context);

    // Create gain node for volume control
    this.gainNode = context.createGain();
    this.gainNode.gain.value = 1.0;

    // Volume object with rampTo support
    const gainNode = this.gainNode;
    const ctx = context;
    this.volume = {
      get value() {
        return gainToDb(gainNode.gain.value);
      },
      set value(db: number) {
        const gain = dbToGain(db);
        gainNode.gain.value = gain;
      },
      rampTo(db: number, rampTime: number, startTime?: number) {
        const gain = dbToGain(db);
        const when = startTime ?? ctx.currentTime;
        gainNode.gain.setValueAtTime(gainNode.gain.value, when);
        gainNode.gain.linearRampToValueAtTime(gain, when + rampTime);
      },
    };

    // Detune object (in cents)
    const self = this;
    this.detune = {
      get value() {
        return self._detuneValue;
      },
      set value(cents: number) {
        self._detuneValue = cents;
        if (self.initialized) {
          self.bridge.setDetune(cents);
        }
      },
    };
  }

  /**
   * Set BPM for duration calculations
   */
  setBPM(bpm: number): void {
    this._bpm = bpm;
  }

  /**
   * Initialize the WASM synth
   * Must be called before playing notes
   */
  async initialize(): Promise<void> {
    await this.bridge.initialize();
    this.bridge.connect(this.gainNode);
    this.initialized = true;

    // Apply any pending detune
    if (this._detuneValue !== 0) {
      this.bridge.setDetune(this._detuneValue);
    }
  }

  /**
   * Check if synth is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Trigger note attack
   * @param note - Note name (e.g., "C4") or MIDI number
   * @param time - Start time (currently ignored, plays immediately)
   * @param velocity - Velocity 0-1 (default 0.8)
   */
  triggerAttack(note: string | number, _time?: number, velocity: number = 0.8): void {
    if (!this.initialized) {
      console.warn('[WASMMonoSynth] Not initialized');
      return;
    }

    // Convert note to MIDI number if string
    const midiNote =
      typeof note === 'string' ? noteNameToMidi(note) : Math.round(note);

    // Convert velocity (0-1) to MIDI velocity (0-127)
    const midiVelocity = Math.floor(Math.max(0, Math.min(1, velocity)) * 127);

    this.currentNote = midiNote;
    this.bridge.noteOn(midiNote, midiVelocity);
  }

  /**
   * Trigger note release
   * @param time - Release time (currently ignored, releases immediately)
   */
  triggerRelease(_time?: number): void {
    if (!this.initialized) {
      return;
    }

    this.bridge.noteOff();
    this.currentNote = null;
  }

  /**
   * Trigger attack and release with duration
   * Compatible with TriggerableSynth interface
   *
   * @param note - Note name (e.g., "C4") or MIDI number
   * @param duration - Duration in seconds or Tone.js notation (e.g., "8n")
   * @param time - Start time (currently plays immediately)
   * @param velocity - Velocity 0-1 (default 0.8)
   */
  triggerAttackRelease(
    note: string | number,
    duration: string | number,
    _time?: number,
    velocity: number = 0.8
  ): void {
    // Trigger attack immediately
    this.triggerAttack(note, _time, velocity);

    // Calculate release time
    const durationSeconds =
      typeof duration === 'string' ? durationToSeconds(duration, this._bpm) : duration;

    // Schedule release using setTimeout
    // Note: This is not sample-accurate, but matches the existing WASM FM synth behavior
    // For precise timing, the AudioScheduler should be used externally
    setTimeout(() => {
      this.triggerRelease();
    }, durationSeconds * 1000);
  }

  /**
   * Connect to audio destination
   */
  connect(destination: AudioNode): this {
    this.gainNode.connect(destination);
    return this;
  }

  /**
   * Disconnect from all destinations
   */
  disconnect(): void {
    this.gainNode.disconnect();
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.disconnect();
    this.bridge.dispose();
    this.initialized = false;
  }

  /**
   * Unsync from transport (no-op for WASM synth)
   */
  unsync(): this {
    return this;
  }

  // ==================== MonoSynth-specific Parameters ====================

  /**
   * Set oscillator type
   * @param type - 0=sine, 1=square, 2=sawtooth, 3=triangle
   */
  setOscillatorType(type: number): void {
    if (this.initialized) {
      this.bridge.setOscillatorType(type);
    }
  }

  /**
   * Set filter cutoff frequency
   * @param cutoff - Cutoff frequency in Hz (20-20000)
   */
  setFilterCutoff(cutoff: number): void {
    if (this.initialized) {
      this.bridge.setFilterCutoff(cutoff);
    }
  }

  /**
   * Set filter resonance (Q factor)
   * @param resonance - Q value (0.5-20)
   */
  setFilterResonance(resonance: number): void {
    if (this.initialized) {
      this.bridge.setFilterResonance(resonance);
    }
  }

  /**
   * Set filter envelope amount
   * @param amount - Amount 0-1
   */
  setFilterEnvAmount(amount: number): void {
    if (this.initialized) {
      this.bridge.setFilterEnvAmount(amount);
    }
  }

  /**
   * Set amplitude envelope
   */
  setAmpEnvelope(attack: number, decay: number, sustain: number, release: number): void {
    if (this.initialized) {
      this.bridge.setAmpEnvelope(attack, decay, sustain, release);
    }
  }

  /**
   * Set filter envelope
   */
  setFilterEnvelope(attack: number, decay: number, sustain: number, release: number): void {
    if (this.initialized) {
      this.bridge.setFilterEnvelope(attack, decay, sustain, release);
    }
  }

  /**
   * Set volume (0-1)
   */
  setVolume(value: number): void {
    if (this.initialized) {
      this.bridge.setVolume(value);
    }
  }

  /**
   * Set portamento time in seconds
   */
  setPortamento(seconds: number): void {
    this.portamento = seconds;
    if (this.initialized) {
      this.bridge.setPortamento(seconds);
    }
  }

  /**
   * Update multiple parameters at once
   */
  updateParams(params: MonoSynthParams): void {
    if (this.initialized) {
      this.bridge.updateParams(params);
    }
  }

  /**
   * Get the underlying bridge for advanced control
   */
  get wasmBridge(): MonoSynthBridge {
    return this.bridge;
  }

  /**
   * Get the gain node for connecting effects
   */
  get output(): GainNode {
    return this.gainNode;
  }

  /**
   * Check if this is a WASM synth
   */
  isWASMSynth(): boolean {
    return true;
  }
}
